import * as THREE from 'three'
import { OcctImportJSResult, LoaderResult, LoadProgress, ModelUnit, UvProjection } from '../types'
import { makeReporter } from './progress'
import { readStepFileInWorker } from './occtWorker'
import { applyUvProjection } from '@/components/ProductProfile/utils/uvGeneration'

/**
 * The length unit the STEP file DECLARES. Informational only — see loadSTEPFile for why
 * it must not be used to scale the geometry.
 *
 * Reads the entities that actually declare a LENGTH_UNIT rather than searching the whole
 * file: 'INCH' appears in plenty of STEP files that are not in inches (as an angle's
 * conversion base, in a product name, in a header comment), and a bare substring match
 * on it is a false positive waiting to happen.
 */
export function declaredStepUnit(fileContent: string): ModelUnit | null {
  const declarations = fileContent.toUpperCase().match(/[^;]*LENGTH_UNIT[^;]*;/g)
  if (!declarations) return null
  for (const declaration of declarations) {
    if (/CONVERSION_BASED_UNIT\s*\(\s*'INCH/.test(declaration)) return 'in'
    if (/SI_UNIT\s*\(\s*\.MILLI\.\s*,\s*\.METRE\./.test(declaration)) return 'mm'
    if (/SI_UNIT\s*\(\s*\.CENTI\.\s*,\s*\.METRE\./.test(declaration)) return 'cm'
    if (/SI_UNIT\s*\(\s*\$?\s*,\s*\.METRE\./.test(declaration)) return 'm'
  }
  return null
}

export async function loadSTEPFile(
  file: File,
  linearDeflection: number,
  uvProjection: UvProjection = 'cylindrical',
  onProgress?: (progress: LoadProgress) => void
): Promise<LoaderResult> {
  const report = makeReporter(onProgress)

  await report('Reading the file', 6)
  const buffer = await file.arrayBuffer()
  const readParams = {
    createUVs: true,
    linearDeflection: linearDeflection,
    // Two independent controls, both needed for a smooth wall:
    //  - linearDeflection (chord error): set finer by default (StepToGlbConverter) to
    //    remove the triangular facets that appeared in the lower-wall blend regions.
    //  - angularDeflection (max angle between adjacent facet normals): governs
    //    CIRCUMFERENTIAL smoothness. A wide cylinder is locally almost flat, so the
    //    chord tolerance alone leaves coarse facets around it — the "bumpy/wavy
    //    wall" artifact that survives even high material roughness. The old code
    //    hardcoded/floored this at a coarse 0.1 rad, so the smoothness slider could
    //    never fix faceting. We loosely tie it to the slider but clamp to a band
    //    that stays smooth (≈0.025 rad) without exploding vertex count (~0.025 rad
    //    ≈ 230k verts on the test tumbler; 0.012 would be ~600k for little gain).
    angularDeflection: Math.min(Math.max(linearDeflection * 2, 0.025), 0.08),
  }

  await report('Tessellating surfaces — this can take a while on large assemblies', 20)
  const result = await tessellate(buffer, readParams)

  if (!result.meshes || result.meshes.length === 0) {
    throw new Error('No meshes found in STEP file')
  }

  await report(`Building ${result.meshes.length} part${result.meshes.length === 1 ? '' : 's'}`, 62)
  let totalVerts = 0

  // 1. Calculate bounding box from ORIGINAL (unmerged) geometry
  const assemblyGroup = new THREE.Group()

  result.meshes.forEach((m) => {
    const g = new THREE.BufferGeometry()
    if (m.attributes?.position?.array) {
      g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(m.attributes.position.array), 3))
    }
    assemblyGroup.add(new THREE.Mesh(g))
  })

  const assemblyBox = new THREE.Box3().setFromObject(assemblyGroup)
  const center = new THREE.Vector3()
  assemblyBox.getCenter(center)

  // Calculate dimensions to determine orientation
  const dimensions = new THREE.Vector3()
  assemblyBox.getSize(dimensions)

  // Determine if model needs rotation to be upright
  // Goal: Make Y-axis vertical (tallest)
  let rotationAxis: 'none' | 'x' | 'y' | 'z' = 'none'
  let rotationAngle = 0

  if (dimensions.x > dimensions.y && dimensions.x > dimensions.z) {
    // Model is laying along X axis, rotate 90° around Z to make it upright
    rotationAxis = 'z'
    rotationAngle = Math.PI / 2
  } else if (dimensions.z > dimensions.y && dimensions.z > dimensions.x) {
    // Model is laying along Z axis, rotate 90° around X to make it upright
    rotationAxis = 'x'
    rotationAngle = -Math.PI / 2
  }

  // 2. Apply rotation to ALL meshes together, then center the entire assembly
  const processed = result.meshes.map((m, i) => {
    totalVerts += m.attributes.position.array.length / 3

    const posArray = new Float32Array(m.attributes.position.array)

    // Apply rotation to this mesh if needed
    if (rotationAxis !== 'none') {
      for (let j = 0; j < posArray.length; j += 3) {
        const x = posArray[j] - center.x
        const y = posArray[j + 1] - center.y
        const z = posArray[j + 2] - center.z

        if (rotationAxis === 'z') {
          // Rotate around Z axis
          const newX = x * Math.cos(rotationAngle) - y * Math.sin(rotationAngle)
          const newY = x * Math.sin(rotationAngle) + y * Math.cos(rotationAngle)
          posArray[j] = newX + center.x
          posArray[j + 1] = newY + center.y
        } else if (rotationAxis === 'x') {
          // Rotate around X axis
          const newY = y * Math.cos(rotationAngle) - z * Math.sin(rotationAngle)
          const newZ = y * Math.sin(rotationAngle) + z * Math.cos(rotationAngle)
          posArray[j + 1] = newY + center.y
          posArray[j + 2] = newZ + center.z
        }
      }
    }

    m.attributes.position.array = posArray
    return {
      id: m.id || `part-${i}`,
      color: m.color ?? ([0.8, 0.8, 0.8] as [number, number, number]),
      mesh: m,
    }
  })

  // 3. After rotation, recalculate bounding box of ENTIRE assembly and center it
  const rotatedAssemblyGroup = new THREE.Group()
  processed.forEach((p) => {
    const g = new THREE.BufferGeometry()
    g.setAttribute('position', new THREE.BufferAttribute(new Float32Array(p.mesh.attributes.position.array), 3))
    rotatedAssemblyGroup.add(new THREE.Mesh(g))
  })

  const rotatedAssemblyBox = new THREE.Box3().setFromObject(rotatedAssemblyGroup)
  const rotatedCenter = new THREE.Vector3()
  rotatedAssemblyBox.getCenter(rotatedCenter)

  // Apply final centering to all meshes together
  processed.forEach((p) => {
    const posArray = p.mesh.attributes.position.array
    for (let j = 0; j < posArray.length; j += 3) {
      posArray[j] -= rotatedCenter.x // Center X horizontally
      posArray[j + 1] -= rotatedAssemblyBox.min.y // Ground Y at zero
      posArray[j + 2] -= rotatedCenter.z // Center Z horizontally
    }
  })

  await report('Generating UVs', 82)
  // Apply UV projection to all meshes
  if (uvProjection !== 'none') {
    processed.forEach((p) => {
      const geom = new THREE.BufferGeometry()
      geom.setAttribute('position', new THREE.BufferAttribute(new Float32Array(p.mesh.attributes.position.array), 3))
      if (p.mesh.attributes.normal?.array) {
        geom.setAttribute('normal', new THREE.BufferAttribute(new Float32Array(p.mesh.attributes.normal.array), 3))
      } else {
        geom.computeVertexNormals()
      }

      applyUvProjection(geom, uvProjection)

      const uvAttr = geom.getAttribute('uv')
      if (uvAttr) {
        // Create a copy of the UV array to ensure it persists
        p.mesh.attributes.uv = { array: new Float32Array(uvAttr.array as Float32Array) }
      }
    })
  }

  // Calculate viewing scale
  const size = new THREE.Vector3()
  assemblyBox.getSize(size)
  const viewingScale = 150 / Math.max(size.x, size.y, size.z)

  // Height of the centered model (bottom at y=0) for camera aiming.
  const rotatedSize = new THREE.Vector3()
  rotatedAssemblyBox.getSize(rotatedSize)

  await report('Preparing the preview', 95)
  return {
    objects: processed,
    vertexCount: totalVerts,
    viewingScale,
    verticalCenter: (rotatedSize.y * viewingScale) / 2,
    // OCCT normalises a STEP model to millimetres on import, whatever unit the file
    // declares — verified against a tumbler whose file declares INCH and whose
    // coordinates come back as 84 x 179 x 84, its real size in mm. Scaling by the
    // declared unit therefore multiplied that model by 25.4 on export. The unit picker
    // in the studio is the override for the rare file this is wrong for.
    unit: 'mm',
  }
}

/**
 * Tessellate in a worker so the page keeps painting; fall back to the main thread if
 * the worker cannot run (which costs the user a frozen tab, but beats not loading at
 * all). The main-thread path needs the script tag in index.html to have loaded.
 */
async function tessellate(
  buffer: ArrayBuffer,
  readParams: { createUVs: boolean; linearDeflection?: number; angularDeflection?: number }
): Promise<OcctImportJSResult> {
  try {
    return await readStepFileInWorker(buffer, readParams)
  } catch (workerError) {
    if (typeof window.occtimportjs === 'undefined') throw workerError
    console.warn('STEP worker unavailable, parsing on the main thread:', workerError)
    const occt = await window.occtimportjs()
    return occt.ReadStepFile(new Uint8Array(buffer), readParams)
  }
}
